import { AHIcon, AHSeparator, AHText, Box, ListItem } from "arkh";
import { useLazyLoadQuery, graphql, useFragment } from "react-relay";
import { FlatList } from "react-native";

import React, { useCallback } from "react";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { listGutterStyle } from "../guterStyle";
import { OrgAdminScreenProps } from "../OrgAdmin/OrgAdmin";
import { OrgButton } from "../OrgButton";
import { useOrgHomeDetailLayout } from "../useOrgHomeDetailContainerStyle";
import { OrgAdminTopicsQuery } from "./__generated__/OrgAdminTopicsQuery.graphql";
import { OrgAdminTopicsListItem_orgTopic$key } from "./__generated__/OrgAdminTopicsListItem_orgTopic.graphql";
import { useIntl } from "react-intl";

function OrgTopicListItem(props: {
  isFirst: boolean;
  isLast: boolean;
  topic: OrgAdminTopicsListItem_orgTopic$key;
  onPress: () => void;
}) {
  const topic = useFragment(
    graphql`
      fragment OrgAdminTopicsListItem_orgTopic on OrgTopic {
        name
        description
        isDefault
        isPrivate
      }
    `,
    props.topic
  );
  return (
    <ListItem
      isFirst={props.isFirst}
      isLast={props.isLast}
      onPress={props.onPress}
      right={
        <>
          {topic.isDefault ? (
            <AHText spaceRight="xsmall">Default</AHText>
          ) : null}
          <ListItem.NavArrow />
        </>
      }
      left={
        topic.isPrivate ? (
          <AHIcon type="locked" size="small" color="text-primary" />
        ) : null
      }
      border="border-primary"
      displayMode="TABLE"
    >
      <Box>
        <Box
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <ListItem.Title>{topic.name}</ListItem.Title>
        </Box>
        <ListItem.SubTitle>{topic.description}</ListItem.SubTitle>
      </Box>
    </ListItem>
  );
}

function InnerOrgAdminTopicsScreen(
  props: OrgAdminScreenProps<"OrgAdminTopics"> & { fetchKey: string }
) {
  const data = useLazyLoadQuery<OrgAdminTopicsQuery>(
    graphql`
      query OrgAdminTopicsQuery {
        organisation @required(action: THROW) {
          id
          name
          topics {
            id
            name
            ...OrgAdminTopicsListItem_orgTopic
          }
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );
  const topics = data.organisation.topics ?? [];
  const { navigateDeprecated } = props.navigation;
  const renderItem = useCallback(
    ({ item, index }: { item: (typeof topics)[number]; index: number }) => {
      return (
        <OrgTopicListItem
          topic={item}
          isFirst={index === 0}
          isLast={index === topics.length - 1}
          onPress={() => {
            navigateDeprecated("OrgAdminTopicsEdit", {
              topicId: item.id,
            });
          }}
        />
      );
    },
    [topics.length, navigateDeprecated]
  );

  const detailLayout = useOrgHomeDetailLayout();

  const intl = useIntl();
  return (
    <Box spaceTop="medium" style={{ flex: 1 }}>
      <FlatList
        style={[
          {
            flex: 1,
          },
          listGutterStyle,
        ]}
        contentContainerStyle={[
          detailLayout.contentContainerStyle,
          { minWidth: 300 },
        ]}
        data={topics}
        keyboardShouldPersistTaps="handled"
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
        ItemSeparatorComponent={AHSeparator}
      />
      <AHSeparator />
      <Box
        color="surface"
        space="medium"
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <OrgButton
          title={intl.formatMessage({
            defaultMessage: "Create Attribute",
            description: "intl_token Create Attribute",
          })}
          onPress={() => {
            navigateDeprecated("OrgAdminTopicsCreate", {});
          }}
          backgroundColor="primary"
          tintColor="text-inverted"
          icon="add"
        />
      </Box>
    </Box>
  );
}

export function OrgAdminTopicsScreen(
  props: OrgAdminScreenProps<"OrgAdminTopics">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey: string) => (
        <InnerOrgAdminTopicsScreen {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}
